import { UpdateUserPayload } from '@/pages/users/common/users-form-dialog';
// import GroupRepresentation from '@keycloak/keycloak-admin-client/lib/defs/groupRepresentation';
import UserRepresentation from '@keycloak/keycloak-admin-client/lib/defs/userRepresentation';
import { removeEmptyValues } from '@/lib/utils';
import { http } from '.';

// Utility to convert snake_case to camelCase
const toCamelCase = (str: string): string => {
  return str.replace(/([-_][a-z])/g, (group) =>
    group.toUpperCase().replace('-', '').replace('_', '')
  );
};

// Convert object keys from snake_case to camelCase recursively
const convertKeysToCamelCase = <T extends object>(obj: T): any => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item));
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      toCamelCase(key),
      convertKeysToCamelCase(value)
    ])
  );
};

// Transform a Kid object from API to frontend format
export const adaptKidFromApi = (kid: any): Kid => {
  const camelCasedKid = convertKeysToCamelCase(kid);
  
  // Create a proper group object if only group_id exists
  const groupObject = kid.group ? {
    _id: kid.group.id || kid.group._id || '',
    id: kid.group.id || kid.group._id || '',
    name: kid.group.name || ''
  } : kid.group_id ? {
    _id: kid.group_id,
    id: kid.group_id,
    name: kid.group_name || '' // Use group_name from API if available
  } : undefined;
  
  // Ensure health data is properly structured
  const healthData = kid.healthData ? {
    allergies: kid.healthData.allergies || '',
    medications: kid.healthData.medications || '',
    medical_conditions: kid.healthData.medical_conditions || '',
    additional_info: kid.healthData.additional_info || ''
  } : undefined;
  
  // Preserve parents array with snake_case keys (API format)
  const parents = kid.parents ? kid.parents : [];
  
  // Return the camelCase converted object with necessary additions
  return {
    ...camelCasedKid,
    _id: kid.id || '',  // Use ID from API as _id for frontend
    id: kid.id || '',   // Keep both id and _id
    signed_in: kid.signed_in, // Preserve original signed_in property from API
    national_id: kid.national_id, // Preserve snake_case key used by the UI
    group: groupObject,
    healthData: healthData,
    parents: parents, // Override with original parents array (snake_case)
    // undefined = field not present in API (try fetching anyway); false = API explicitly says no avatar
    hasAvatar: (kid.has_avatar != null ? kid.has_avatar === true : kid.hasAvatar != null ? kid.hasAvatar === true : undefined),
  };
};

export interface KidsListResponse {
  kids: Kid[];
  total: number;
  page: number;
  limit: number;
}

export interface ParentInfo {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
}

export interface Kid {
  _id: string;
  firstName: string;
  lastName: string;
  group: Group;
  group_id: number;
  group_name?: string; // Add group_name field from API
  age: number;
  gender: string;
  signed_in: boolean;
  created_at: string;
  birthday?: string;
  national_id?: string; // JMBG in Serbian
  parents: ParentInfo[];
  packageId?: string | null;
  subsidies?: number | null;
  contractOwner?: string | null;
  enrollmentStatus?: string; // 'pending', 'approved', 'rejected', etc. (camelCase from API)
  enrollmentRequestData?: {
    kid: any;
    parent: {
      email: string;
      phone: string;
      address: string;
      last_name: string;
      first_name: string;
    };
    submitted_at: string;
  };
  requestedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  healthData?: {
    allergies: string;
    medications: string;
    medical_conditions: string;
    additional_info: string;
  };
  hasAvatar?: boolean;
}

// Document related interfaces
export interface KidDocument {
  id: string;
  kidId: string;
  kidName?: string;
  organizationId?: string;
  institutionId?: string;
  title: string;
  documentType: DocumentType;
  documentCategory?: string;
  originalFilename: string;
  fileSize: string; // API returns as string
  mimeType: string;
  description?: string;
  notes?: string;
  isVisibleToParents: boolean;
  isSensitive: boolean;
  isArchived: boolean;
  accessCount: number;
  lastAccessedAt?: string | null;
  uploadedByUserId: string;
  uploadedByRole: string;
  createdAt: string;
  updatedAt: string;
}

export type DocumentType = 'medical' | 'legal' | 'educational' | 'photo' | 'other';

export interface DocumentUploadRequest {
  file: File;
  documentType: DocumentType;
  documentCategory?: string;
  title: string;
  isVisibleToParents?: boolean;
  isSensitive?: boolean;
}

export interface DocumentListResponse {
  documents: KidDocument[];
  total: number;
  page: number;
  limit: number;
}

export interface DocumentTypesResponse {
  documentTypes: {
    type: DocumentType;
    categories: string[];
    maxSizeBytes: number;
    allowedMimeTypes: string[];
    description: string;
  }[];
}

export const fetchChildrenList = async ({
  page,
  limit,
  filterBy,
}: {
  page: number;
  limit: number;
  filterBy?: string | Record<string, any>;
}) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  // Process filterBy parameter (without institution_id)
  if (filterBy) {
    let filterObject: Record<string, any> = {};
    
    // If filterBy is already a string, parse it
    if (typeof filterBy === 'string') {
      try {
        filterObject = JSON.parse(filterBy);
      } catch (e) {
        console.error('Failed to parse filterBy string:', e);
      }
    } 
    // If filterBy is an object, use it directly
    else if (typeof filterBy === 'object' && filterBy !== null) {
      filterObject = { ...filterBy };
    }
    
    // Convert filterObject back to a JSON string and add to params
    params.append('filterBy', JSON.stringify(filterObject));
  }
  
  const response = await http.get<{ kids: any[], page: number | string, total: number, limit: number | string }>('/kids', {
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });

  // Transform the kids from API format
  const adaptedKids = response.data.kids.map(adaptKidFromApi);
  
  // Note: Group names should come from the API response directly
  // If group names are missing, the backend should provide them

  // Return the transformed response
  return {
    ...response,
    data: {
      kids: adaptedKids,
      page: Number(response.data.page),
      total: response.data.total,
      limit: Number(response.data.limit)
    }
  };
};

export const fetchChild = async (id: string, filterBy?: Record<string, any>) => {
  const params = new URLSearchParams();
  
  // If there are additional filters, add them to filterBy
  if (filterBy && Object.keys(filterBy).length > 0) {
    params.append('filterBy', JSON.stringify(filterBy));
  }
  
  const response = await http.get<any>(`/kids/${id}`, {
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });

  // Transform the kid data from API
  const adaptedKid = adaptKidFromApi(response.data);
  
  // Note: Group names should come from the API response directly
  // If group names are missing, the backend should provide them

  // Return the transformed response
  return {
    ...response,
    data: adaptedKid
  };
};

export type CreateKidPayload = {
  first_name: string;
  last_name: string;
  gender: 'male' | 'female' | 'other';
  birthday: string;
  institution_id: string;
  age?: number;
  signed_in?: boolean;
  group_id?: string;
  package_id?: string;
  subsidies?: number;
  contract_owner?: string | null;
  national_id?: string;
  parents?: string[];
  healthData?: {
    allergies: string;
    medications: string;
    medical_conditions: string;
    additional_info: string;
  };
};

export const createChild = async (child: CreateKidPayload) => {
  // Remove empty values before sending to backend
  const cleanedChild = removeEmptyValues(child);
  return http.post<Kid>('/kids/create', cleanedChild, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const deleteChild = async (childId: string) => {
  return http.delete<Kid>(`/kids/${childId}`);
};

export const uploadKidAvatar = async (kidId: string, file: File): Promise<{ success: boolean; kidId: string }> => {
  const formData = new FormData();
  formData.append('avatar', file);
  const response = await http.put<{ success: boolean; kidId: string }>(
    `/kids/${kidId}/avatar`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const deleteKidAvatar = async (kidId: string): Promise<{ success: boolean }> => {
  const response = await http.delete<{ success: boolean }>(`/kids/${kidId}/avatar`);
  return response.data;
};

export const fetchKidAvatar = async (kidId: string): Promise<string> => {
  const response = await http.get(`/kids/${kidId}/avatar`, { responseType: 'blob' });
  return URL.createObjectURL(response.data);
};

export const uploadUserAvatar = async (userId: string, file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('avatar', file);
  await http.put(`/users/${userId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const fetchUserAvatar = async (userId: string): Promise<string> => {
  const response = await http.get(`/users/${userId}/avatar`, { responseType: 'blob' });
  return URL.createObjectURL(response.data);
};

export const deleteUserAvatar = async (userId: string): Promise<void> => {
  await http.delete(`/users/${userId}/avatar`);
};

export interface Group {
  _id?: string;  // Optional for backward compatibility
  id?: string;   // Optional for backward compatibility
  name: string;
  type?: string;
  teachers?: (string | { 
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    access_role?: string;
  })[];
  memberAccess?: GroupMemberAccess[];
  institution_id?: string;
  organization_id?: string;
  created_at?: string;
  updated_at?: string;
}

// Helper function to safely get group ID, ensuring we always get a string
export const getGroupId = (group: Group): string => {
  return (group.id || group._id || '').toString();
};

// Global cache for groups to prevent duplicate API calls
let groupsRequestCounter = 0; // DEBUG: Track total requests (will be removed after debugging)

const groupsCache: {
  data: Group[] | null;
  loading: boolean;
  promise: Promise<Group[]> | null;
  lastFetch: number;
  lastInstitutionId: string | null | undefined;
} = {
  data: null,
  loading: false,
  promise: null,
  lastFetch: 0,
  lastInstitutionId: null
};

// Cache duration: 3 minutes
const GROUPS_CACHE_DURATION = 3 * 60 * 1000;

// Function to clear groups cache
export const clearGroupsCache = () => {
  console.log('[GROUPS CACHE] Clearing groups cache and forcing fresh data');
  groupsCache.data = null;
  groupsCache.loading = false;
  groupsCache.promise = null;
  groupsCache.lastFetch = 0;
  groupsCache.lastInstitutionId = null;
  
  // Force next request to bypass all caches by resetting cache timestamp
  groupsCache.lastFetch = 0;
};

export const fetchGroupsList = async (institutionId?: string) => {
  const params = new URLSearchParams();
  const organizationId = localStorage.getItem('currentOrganizationId');
  
  let currentInstitutionId = institutionId;
  
  // If no explicit institutionId was provided, try to get it from localStorage for caching
  if (!institutionId) {
    const savedInstitutionJson = localStorage.getItem('currentInstitution');
    if (savedInstitutionJson) {
      try {
        const savedInstitution = JSON.parse(savedInstitutionJson);
        if (savedInstitution && savedInstitution.id) {
          currentInstitutionId = savedInstitution.id;
        }
      } catch (e) {
        console.error('Failed to parse saved institution:', e);
      }
    }
  }
  
  // Early return if no institution ID is available to prevent 400 errors
  if (!currentInstitutionId) {
    console.log('[GROUPS] No institution ID available, returning empty data');
    // Return empty data rather than making a failed request
    return { data: [] };
  }
  
  console.log('[GROUPS] Starting fetch for institution:', currentInstitutionId, 'at', new Date().toISOString());
  
  // DEBUG: Track and log all request details (only for valid requests)
  groupsRequestCounter++;
  console.log(`[GROUPS DEBUG #${groupsRequestCounter}] Request details:`, {
    institutionId,
    currentInstitutionId,
    organizationId,
    cacheLastInstitutionId: groupsCache.lastInstitutionId,
    cacheLoading: groupsCache.loading,
    cacheDataExists: !!groupsCache.data,
    totalRequestsSoFar: groupsRequestCounter,
    timestamp: new Date().toISOString(),
    stackTrace: new Error().stack?.split('\n').slice(1, 4).map(line => line.trim())
  });
  
  // Note: institutionId is automatically added by the HTTP interceptor

  const now = Date.now();
  const isCacheValid = groupsCache.data && 
    (now - groupsCache.lastFetch) < GROUPS_CACHE_DURATION &&
    groupsCache.lastInstitutionId === currentInstitutionId;

  // If we have valid cached data, use it immediately
  if (isCacheValid) {
    console.log('[GROUPS] Using cached data');
    return { data: groupsCache.data! };
  }

  // If there's already a request in progress for the same institution, wait for it
  if (groupsCache.loading && groupsCache.promise && groupsCache.lastInstitutionId === currentInstitutionId) {
    console.log('[GROUPS] Request in progress, waiting...');
    const data = await groupsCache.promise;
    return { data };
  }

  console.log('[GROUPS] Loading groups data... (institutionId:', currentInstitutionId, ', organizationId:', organizationId, ')');
  
  // Set loading state and cache the promise
  groupsCache.loading = true;
  groupsCache.lastInstitutionId = currentInstitutionId;
  
  const loadGroups = async (): Promise<Group[]> => {
    try {
      console.log('[GROUPS DEBUG] Making HTTP request with params:', {
        url: '/groups',
        params: Object.fromEntries(params),
        headers: { 'skip-org-headers': 'true', 'Cache-Control': 'no-cache' },
        cacheBuster: 'Added to force fresh data'
      });
      
      // Add cache busting parameter to force fresh data
      const cacheBuster = Date.now();
      params.set('_t', cacheBuster.toString());
      
      const response = await http.get<Group[]>('/groups', {
        params,
        headers: {
          'skip-org-headers': 'true',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      console.log('[GROUPS DEBUG] HTTP response:', {
        status: response.status,
        dataLength: response.data?.length,
        institutionId: currentInstitutionId
      });

      // Update cache
      groupsCache.data = response.data;
      groupsCache.lastFetch = Date.now();
      groupsCache.loading = false;
      groupsCache.promise = null;

      console.log('[GROUPS] Groups loaded and cached');
      return response.data;
    } catch (error: any) {
      console.log('[GROUPS DEBUG] HTTP error:', {
        status: error?.response?.status,
        message: error?.message,
        institutionId: currentInstitutionId,
        organizationId
      });
      
      groupsCache.loading = false;
      groupsCache.promise = null;
      console.error('Failed to fetch groups:', error);
      throw error;
    }
  };

  groupsCache.promise = loadGroups();
  const data = await groupsCache.promise;
  return { data };
};

export const fetchGroup = async (id: string) => {
  return http.get<Group>(`/groups/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const deleteGroup = async (groupId: string) => {
  return http.delete<Group>(`/groups/${groupId}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export interface Teacher {
  _id: string;
  name: string;
  lastname: string;
  email: string;
  tel: string;
  role: string;
}

export type CreateGroupPayload = Pick<Group, 'name' | 'type'> & {
  teachers: string[];
  institutionId: string;
};

export const createGroup = async (group: CreateGroupPayload) => {
  // Remove empty values before sending to backend
  const cleanedGroup = removeEmptyValues(group);
  return http.post<Group>('/groups', cleanedGroup, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export type UpdateGroupPayload = Group;

export const updateGroup = async (id: string, group: UpdateGroupPayload) => {
  // Remove empty values before sending to backend
  const cleanedGroup = removeEmptyValues(group);
  return http.put<Group>(`/groups/${id}`, cleanedGroup, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export type TaskStatus = 'todo' | 'in-progress' | 'done' | 'canceled';
export type TaskPriority = 'low' | 'medium' | 'high';
export type TaskLabel =
  | 'activity'
  | 'plan'
  | 'documentation'
  | 'learning'
  | 'teaching';

export interface TaskUser {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface TaskResponse {
  _id: string;
  name: string;
  summary: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  label?: TaskLabel;
  assignee: TaskUser;
  reporter?: TaskUser;
  createdAt: string;
  updatedAt: string;
}

export const fetchTasksList = async () => {
  return http.get<TaskResponse[]>('/tasks', {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

export type CreateTaskPayload = Omit<
  TaskResponse,
  '_id' | 'name' | 'status' | 'assignee' | 'reporter'
> & {
  assignee: string;
  reporter?: string;
};

export const createTask = async (task: CreateTaskPayload) => {
  return http.post<TaskResponse>('/tasks/create', task, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

export type UpdateTaskPayload = Partial<CreateTaskPayload> & {
  status?: TaskStatus;
};

export const updateTask = async (id: string, task: UpdateTaskPayload) => {
  return http.put<TaskResponse>(`/tasks/${id}`, task, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

export const deleteTask = async (id: string) => {
  return http.delete<TaskResponse>(`/tasks/${id}`, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

// Task Comments interfaces and endpoints
export interface TaskComment {
  id: string;
  taskId: string;
  content: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  isEdited: boolean;
  isDeleted: boolean;
  organizationId: string;
}

export interface CreateTaskCommentPayload {
  content: string;
}

export interface UpdateTaskCommentPayload {
  content: string;
}

export interface TaskCommentsResponse {
  comments: TaskComment[];
  count: number;
}

// Get comments for a task
export const fetchTaskComments = async (taskId: string) => {
  return http.get<TaskCommentsResponse>(`/tasks/${taskId}/comments`, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

// Add comment to a task
export const createTaskComment = async (taskId: string, payload: CreateTaskCommentPayload) => {
  return http.post<TaskComment>(`/tasks/${taskId}/comments`, payload, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

// Edit a comment
export const updateTaskComment = async (taskId: string, commentId: string, payload: UpdateTaskCommentPayload) => {
  return http.put<TaskComment>(`/tasks/${taskId}/comments/${commentId}`, payload, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

// Delete a comment
export const deleteTaskComment = async (taskId: string, commentId: string) => {
  return http.delete(`/tasks/${taskId}/comments/${commentId}`, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

// Get single comment
export const fetchTaskComment = async (taskId: string, commentId: string) => {
  return http.get<TaskComment>(`/tasks/${taskId}/comments/${commentId}`, {
    headers: {
      'skip-org-headers': 'true' // Skip automatic org/institution headers
    }
  });
};

export const fetchUsers = async () => {
  // Get the current organization ID from localStorage
  const organizationId = localStorage.getItem('currentOrganizationId');
  console.log(`Fetching users for organization ${organizationId}`);
  
  return http.get<UserRepresentation[]>('/users', {
    headers: {
      'X-Organization-ID': organizationId || ''
    }
  });
};

// Available roles for user creation
export const AVAILABLE_ROLES = [
  { value: 'admin', labelKey: 'users.roles.admin', icon: '🔒' },
  { value: 'teacher', labelKey: 'users.roles.teacher', icon: '👩‍🏫' },
  { value: 'employee', labelKey: 'users.roles.employee', icon: '👷' },
  { value: 'parent', labelKey: 'users.roles.parent', icon: '👨‍👩‍👧‍👦' },
  { value: 'finance', labelKey: 'users.roles.finance', icon: '💰' },
  { value: 'supervisor', labelKey: 'users.roles.supervisor', icon: '👔' }
];

// Validation schema for user creation
export const USER_VALIDATION_SCHEMA = {
  name: { required: true, minLength: 1, maxLength: 50 },
  lastname: { required: true, minLength: 1, maxLength: 50 },
  email: { required: true, format: 'email' },
  tel: { required: true, minLength: 1 },
  roles: { required: true, type: 'array', minLength: 1, enum: AVAILABLE_ROLES.map(r => r.value) },
  children: { optional: true, type: 'array' },
  googleSignIn: { optional: true, type: 'boolean' }
};

export type CreateUserPayload = {
  name: string;
  lastname: string;
  email: string;
  tel: string;
  roles: string[];
  children?: string[];
  googleSignIn?: boolean;
  // Optional extended profile fields
  addressCity?: string;
  addressStreet?: string;
  addressStreetNumber?: string;
  jmbg?: string;
  idCardNumber?: string;
  idCardIssuedBy?: string;
};

export type InviteUserPayload = {
  email: string;
  organizationRoles: string[];
  institutionIds: string[];
  name?: string;
  lastname?: string;
  message?: string;
};

export type InviteUserResponse = {
  message: string;
  invitationId: string;
};

// Frontend payload type (camelCase) for easier use in React components
export type UpdateChildPayload = Partial<
  Omit<Kid, 'group'> & {
    group?: string;
    healthData?: {
      allergies: string;
      medications: string;
      medical_conditions: string;
      additional_info: string;
    };
  }
>;

// Backend payload type (snake_case) - what the API actually expects
export type UpdateChildBackendPayload = {
  first_name?: string;
  last_name?: string;
  gender?: 'male' | 'female' | 'other';
  birthday?: string;
  group_id?: string;
  package_id?: string;
  subsidies?: number | null;
  contract_owner?: string | null;
  national_id?: string;
  signed_in?: boolean;
  healthData?: {
    allergies: string;
    medications: string;
    medical_conditions: string;
    additional_info: string;
  };
};

// Transform function to convert frontend payload to backend format
const transformUpdateChildPayload = (payload: UpdateChildPayload): UpdateChildBackendPayload => {
  const transformed: UpdateChildBackendPayload = {};
  
  // Map camelCase to snake_case
  if (payload.firstName !== undefined) transformed.first_name = payload.firstName;
  if (payload.lastName !== undefined) transformed.last_name = payload.lastName;
  if (payload.gender !== undefined) transformed.gender = payload.gender as 'male' | 'female' | 'other';
  if (payload.birthday !== undefined) transformed.birthday = payload.birthday;
  if (payload.group !== undefined) transformed.group_id = payload.group;
  if (payload.packageId !== undefined) transformed.package_id = payload.packageId || undefined;
  if (payload.subsidies !== undefined) transformed.subsidies = payload.subsidies;
  if (payload.contractOwner !== undefined) transformed.contract_owner = payload.contractOwner || undefined;
  if (payload.national_id !== undefined) transformed.national_id = payload.national_id;
  if (payload.signed_in !== undefined) transformed.signed_in = payload.signed_in;
  if (payload.healthData !== undefined) transformed.healthData = payload.healthData;
  
  return transformed;
};

export const updateChild = async (
  childId: string,
  child: UpdateChildPayload,
  filterBy?: Record<string, any>
) => {
  const params = new URLSearchParams();
  
  // If there are additional filters, add them to filterBy
  if (filterBy && Object.keys(filterBy).length > 0) {
    params.append('filterBy', JSON.stringify(filterBy));
  }
  
  // Transform camelCase to snake_case format for backend
  const transformedPayload = transformUpdateChildPayload(child);
  
  // Remove empty values before sending to backend
  const cleanedChild = removeEmptyValues(transformedPayload);
  
  return http.put(`/kids/${childId}`, cleanedChild, {
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const assignParent = async (childId: string, parentId: string, filterBy?: Record<string, any>) => {
  const params = new URLSearchParams();
  
  // If there are additional filters, add them to filterBy
  if (filterBy && Object.keys(filterBy).length > 0) {
    params.append('filterBy', JSON.stringify(filterBy));
  }
  
  return http.post<Kid>(`/kids/${childId}/assign-parent`, { parentId }, {
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const unassignParent = async (childId: string, parentId: string, filterBy?: Record<string, any>) => {
  const params = new URLSearchParams();
  
  if (filterBy) {
    params.append('filterBy', JSON.stringify(filterBy));
  }
  
  return http.post<Kid>(`/kids/${childId}/unassign-parent`, { parentId }, {
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const createUser = async (user: CreateUserPayload) => {
  // Remove empty values before sending to backend
  const cleanedUser = removeEmptyValues(user);
  return http.post<UserRepresentation>('/users/create', cleanedUser, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const inviteUser = async (invitation: InviteUserPayload) => {
  // Remove empty values before sending to backend
  const cleanedInvitation = removeEmptyValues(invitation);
  return http.post<InviteUserResponse>('/users/invite', cleanedInvitation);
};

export const deleteUser = async (id: string) => {
  return http.delete<UserRepresentation>(`/users/${id}`);
};

export const removeUserFromInstitution = async (userId: string) => {
  return http.delete(`/users/${userId}/institutions`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};


export const fetchEmployees = async () => {
  try {
    // Get the current institution ID from localStorage - always get the latest value
    const currentInstitutionString = localStorage.getItem('currentInstitution');
    const currentInstitution = currentInstitutionString ? JSON.parse(currentInstitutionString) : null;
    const institutionId = currentInstitution?.id || '';
    
    console.log(`Fetching employees for institution ${institutionId}`);
  
    if (!institutionId) {
      console.warn('No institution ID found in localStorage');
      return { data: [] };
    }
    
    const response = await http.get<UserRepresentation[]>('/users/staff', {
      params: { 
        institutionId
      }
    });
    
    // Ensure response.data is always an array
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response from staff API:', response.data);
      // Return empty array if data is invalid
      return { data: [] };
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching employees:', error);
    // Re-throw the error to be handled by React Query
    throw error;
  }
};

export const fetchParents = async () => {
  console.log('Fetching parents');
  
  return http.get<UserRepresentation[]>('/users/parents', {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const editUser = async (user: UpdateUserPayload, userId: string) => {
  try {
    return http.put<UserRepresentation>(`/users/employees/${userId}`, user, {
      headers: {
        'skip-org-headers': 'true',
      },
    });
  } catch (error) {
    console.error('Error editing user:', error);
    throw error;
  }
};

export type UpdateParentProfilePayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  tel?: string;
  groups?: string[];
  addressCity?: string;
  addressStreet?: string;
  addressStreetNumber?: string;
  jmbg?: string;
  idCardNumber?: string;
  idCardIssuedBy?: string;
};

export const updateParentProfile = async (
  userId: string,
  payload: UpdateParentProfilePayload,
) => {
  return http.put<UserRepresentation>(
    `/users/parents/${userId}`,
    payload,
    {
      headers: {
        'skip-org-headers': 'true',
      },
    },
  );
};

// New type for profile updates that includes bio
export type UpdateProfilePayload = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  attributes?: {
    tel?: string;
    phone?: string;
    bio?: string;
  };
};

// Update current user's profile
export const updateCurrentUserProfile = async (profile: UpdateProfilePayload) => {
  try {
    return http.put<UserRepresentation>('/users/profile', profile);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const editParent = async (
  id: string,
  parent: Partial<UserRepresentation>,
) => {
  return http.put<UserRepresentation>(`/users/parents/${id}`, parent);
};

export const deleteParent = async (id: string) => {
  return http.delete<UserRepresentation>(`/users/parents/${id}`);
};

export const fetchTeachers = async () => {
  try {
    console.log('Fetching teachers with role filter');
    
    const response = await http.get<UserRepresentation[]>('/users/staff', {
      params: {
        role: 'teacher'  // Keep the role filter as backend expects it
      },
      headers: {
        'skip-org-headers': 'true'  // Remove org/institution headers
      }
    });
    
    // Ensure response.data is always an array
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response from teachers API:', response.data);
      // Return empty array if data is invalid
      return { data: [] };
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching teachers:', error);
    // Re-throw the error to be handled by React Query
    throw error;
  }
};

export const fetchContractors = async () => {
  // Get the current organization ID from localStorage
  const organizationId = localStorage.getItem('currentOrganizationId');
  console.log(`Fetching contractors for organization ${organizationId}`);
  
  return http.get<UserRepresentation[]>('/users/contractors', {
    headers: {
      'X-Organization-ID': organizationId || ''
    }
  });
};

export const fetchUsersByEmail = async (email: string, group?: string) => {
  const params = new URLSearchParams();
  if (group) {
    params.append('group', group);
  }
  params.append('email', email);

  return http.get<UserRepresentation[]>('/users/search', { params });
};

// New function for searching parents by first/last name
export const fetchParentsByName = async (search: string) => {
  const params = new URLSearchParams();
  
  // Get institution ID from localStorage
  const currentInstitutionString = localStorage.getItem('currentInstitution');
  const currentInstitution = currentInstitutionString ? JSON.parse(currentInstitutionString) : null;
  const institutionId = currentInstitution?.id;
  
  if (!institutionId) {
    throw new Error('Institution ID is required for parent search');
  }
  
  params.append('institutionId', institutionId);
  params.append('search', search);

  return http.get<UserRepresentation[]>('/users/parents/search', { params });
};

export interface Package {
  _id: string;
  name: string;
  price: number;
  description: string;
}

export const fetchPackagesList = async () => {
  return http.get<Package[]>('/finances/packages', {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const fetchPackage = async (id: string) => {
  return http.get<Package>(`/finances/packages/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const createPackage = async (pkg: Omit<Package, '_id'>) => {
  return http.post<Package>('/finances/packages', pkg, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const updatePackage = async (id: string, pkg: Partial<Package>) => {
  return http.put<Package>(`/finances/packages/${id}`, pkg, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const deletePackage = async (id: string) => {
  return http.delete<Package>(`/finances/packages/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export interface InvoiceItem {
  kidId: string;
  kidFirstName: string;
  kidLastName: string;
  amount: number;
  packagePrice?: number;
  subsidies?: number;
  daysInMonth?: number;
  daysAttended?: number;
  isProrated?: boolean;
  _id: string;
  attendance?: {
    presentDays: string[];   // ISO date strings "YYYY-MM-DD"
    presentDaysCount: number;
  };
}

export interface Invoice {
  _id: string;
  invoiceNumber?: string; // Add optional invoice number field like "BAB-2025-002"
  month: string;
  year?: string;
  items: InvoiceItem[];
  mailAddress: string;
  phoneNumber: string;
  total?: number;
  createdAt: string;
  updatedAt?: string;
  status: 'draft' | 'sent' | 'paid' | 'unpaid' | 'overdue' | 'cancelled';
  contractOwnerId: string;
  contractOwnerFirstName: string;
  contractOwnerLastName: string;
  amount: number;
  paid: boolean;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  dueDate?: string;
  paymentTerms?: number;
  finalizedAt?: string | null;
  sentAt?: string | null;
  paidAt?: string | null;
  organizationId?: string;
  generatedAutomatically?: boolean;
  generationJobId?: string | null;
  notes?: string | null;
  ipsQRCode?: {
    qrCodeDataURL: string;
    qrCodeSVG: string;
    ipsString: string;
    accountNumber: string;
    generatedAt: string;
    _id: string;
  };
  organization?: {
    id: string;
    name: string;
    companyDetails?: {
      company_name?: string;
      pib?: string;
      mb?: string;
      registration_number?: string | null;
      address?: {
        street?: string;
        city?: string;
        postal_code?: string;
        country?: string;
        state_province?: string | null;
      };
      contact?: {
        phone?: string;
        email?: string;
        website?: string;
      };
      business?: {
        business_type?: string;
        tax_number?: string | null;
        vat_number?: string | null;
      };
    };
    bankAccounts?: {
      id: string;
      organization_id: string;
      bank_name: string;
      account_number: string;
      iban?: string | null;
      swift_bic?: string | null;
      currency: string;
      is_primary: boolean;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    }[];
  };
}

export const getInvoices = async (month: string): Promise<Invoice[]> => {
  const response = await http.get<Invoice[]>('/finances/invoices', { 
    params: { month },
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const getInvoice = async (id: string): Promise<Invoice> => {
  const response = await http.get<Invoice>(`/finances/invoices/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const updateInvoiceStatus = async (id: string, status: string): Promise<Invoice> => {
  const response = await http.patch<Invoice>(`/finances/invoices/${id}/status`, { status }, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const finalizeInvoice = async (id: string): Promise<Invoice> => {
  const response = await http.post<Invoice>(`/finances/invoices/${id}/finalize`, {}, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const revertInvoiceToDraft = async (id: string): Promise<Invoice> => {
  const response = await http.post<Invoice>(`/finances/invoices/${id}/revert-to-draft`, {}, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const downloadInvoicePdf = async (id: string): Promise<{ blob: Blob; filename: string }> => {
  const response = await http.get(`/finances/invoices/${id}/pdf`, {
    responseType: 'blob',
    headers: {
      'skip-org-headers': 'true'
    }
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `faktura-${id}.pdf`;
  return { blob: response.data, filename };
};

export const markInvoiceSent = async (id: string): Promise<Invoice> => {
  const response = await http.post<Invoice>(`/finances/invoices/${id}/mark-sent`, {}, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export interface UpdateInvoicePayload {
  items?: Array<{
    kidId: string;
    packagePrice?: number;
    subsidies?: number;
  }>;
  notes?: string;
  dueDate?: string;
  paymentTerms?: number;
}

export const updateInvoice = async (id: string, payload: UpdateInvoicePayload): Promise<Invoice & { warning?: string }> => {
  const response = await http.patch<Invoice & { warning?: string }>(`/finances/invoices/${id}`, payload, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

export const regenerateInvoice = async (id: string): Promise<Invoice> => {
  const response = await http.post<Invoice>(`/finances/invoices/${id}/regenerate`, {}, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const generateInvoices = async (month: string, regenerateExisting?: boolean): Promise<void> => {
  const params: Record<string, string> = { month };
  if (regenerateExisting) {
    params.regenerateExisting = 'true';
  }
  await http.get('/finances/invoices/generate', { 
    params,
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const fetchTotalAmount = async (month: string) => {
  return http.get<{ totalAmount: number }>('/finances/invoices/total-amount', {
    params: { month },
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export interface UnpaidInvoice {
  contractOwnerFirstName: string;
  contractOwnerLastName: string;
  mailAddress: string;
  amount: number;
}

export const fetchUnpaidInvoices = async (month: string) => {
  return http.get<UnpaidInvoice[]>('/finances/invoices/unpaid', {
    params: { month },
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Bulk Invoice Summary Types and Functions
export interface StatusBreakdown {
  status: string;
  count: number;
  totalAmount: number;
  invoices: Invoice[];
}

export interface MonthSummary {
  month: string;
  totalInvoices: number;
  totalAmount: number;
  statusBreakdown: StatusBreakdown[];
  unpaidInvoices: Invoice[];
  allInvoices: Invoice[];
}

export interface BulkInvoiceSummaryResponse {
  success: boolean;
  data: MonthSummary[];
  monthsRequested: number;
}

export interface BulkInvoiceSummaryParams {
  months: string[];
}

export const fetchBulkInvoiceSummary = async (params: BulkInvoiceSummaryParams): Promise<BulkInvoiceSummaryResponse> => {
  const { months } = params;
  
  if (months.length === 0 || months.length > 12) {
    throw new Error('Must provide 1-12 months');
  }
  
  // Note: institutionId is automatically added by the request interceptor
  // so we don't need to manually add it here
  const response = await http.get<BulkInvoiceSummaryResponse>('/finances/invoices/bulk-summary', {
    params: {
      months: months.join(','),
    },
  });
  return response.data;
};

// Present Children Types and Functions
export interface PresentChildrenResponse {
  presentCount: number;
  totalWithRecords: number;
  children: Array<{
    childId: string;
    name: string;
    surname: string;
    isPresent: boolean;
    arrivalTime?: string;
    departureTime?: string;
    recordedBy?: string;
    notes?: string;
  }>;
}

export const fetchPresentChildren = async (date?: string): Promise<PresentChildrenResponse> => {
  const params = new URLSearchParams();
  
  // Note: Using skip-org-headers, no longer need institution ID
  // let institutionId = null;
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }
  
  // Note: No longer adding institutionId parameter due to skip-org-headers
  // if (institutionId) {
  //   params.append('institutionId', institutionId);
  // }
  
  // Only add date parameter if it's not today
  if (date) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedToday = `${year}-${month}-${day}`;
    
    // Only add date param if it's different from today
    if (date !== formattedToday) {
      params.append('date', date);
    }
  }
  
  const response = await http.get<PresentChildrenResponse>('/metrics/children/present', {
    params,
  });
  return response.data;
};

// Optimized function to fetch present children data for dashboard
export const fetchPresentChildrenForDashboard = async (): Promise<{
  today: PresentChildrenResponse | null;
  yesterday: PresentChildrenResponse | null;
  percentageChange: number;
}> => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayFormatted = yesterday.toISOString().slice(0, 10); // YYYY-MM-DD
  
  console.log('[Dashboard] Fetching present children data for today and yesterday');
  const [todayPresence, yesterdayPresence] = await Promise.allSettled([
    fetchPresentChildren(), // Today (no date param needed)
    fetchPresentChildren(yesterdayFormatted), // Yesterday
  ]);
  
  const todayData = todayPresence.status === 'fulfilled' ? todayPresence.value : null;
  const yesterdayData = yesterdayPresence.status === 'fulfilled' ? yesterdayPresence.value : null;
  
  // Calculate percentage change in present children
  let presentPercentageChange = 0;
  if (yesterdayData && yesterdayData.presentCount > 0 && todayData) {
    presentPercentageChange = ((todayData.presentCount - yesterdayData.presentCount) / yesterdayData.presentCount) * 100;
  } else if (todayData && todayData.presentCount > 0 && (!yesterdayData || yesterdayData.presentCount === 0)) {
    presentPercentageChange = 100; // 100% increase if yesterday was 0
  }
  
  return {
    today: todayData,
    yesterday: yesterdayData,
    percentageChange: Math.round(presentPercentageChange * 10) / 10, // Round to 1 decimal
  };
};

export const fetchGroupActivities = async (
  groupId: string,
  startDate?: string,
  endDate?: string,
) => {
  const params = new URLSearchParams();
  const organizationId = localStorage.getItem('currentOrganizationId');
  
  if (startDate) {
    params.append('startDate', startDate);
  }
  
  if (endDate) {
    params.append('endDate', endDate);
  }
  
  return http.get(`/groups/${groupId}/activities`, {
    params,
    headers: {
      'x-organization-id': organizationId || ''
    }
  });
};

export const fetchKidActivitiesForDay = async (kidId: string, date: string) => {
  return http.get(`/kids/${kidId}/activities/${date}`);
};

// Add a new daily attendance report API function
export interface DailyAttendanceData {
  childId: string;
  whatTheyAte: {
    breakfast: boolean;
    lunch: boolean;
    snack1: boolean;
    snack2: boolean;
  };
  naptime: boolean;
  note: string;
  date?: string;
}

export const logDailyAttendance = async (data: DailyAttendanceData) => {
  return http.post('/metrics/children', data);
};

// Consumption level type for meals
export type ConsumptionLevel = 'none' | 'low' | 'normal' | 'high';

// New function for bulk saving of metrics with dynamic meals
export interface BulkMetricsData {
  date: string;
  metrics: Array<{
    childId: string;
    meals: Array<{
      eventId: string;
      recorded?: boolean; // Keep for backward compatibility
      consumptionLevel?: ConsumptionLevel; // New field: none, low, normal, high
      totalConsumed?: number;
      totalOffered?: number;
    }>;
    naptime: boolean;
    note?: string;
    health_note?: string;
    mood?: { mood?: string; note?: string };
  }>;
}

export const bulkSaveDailyAttendance = async (data: BulkMetricsData) => {
  console.log(`[API DEBUG] Saving bulk daily attendance for date: ${data.date}`, {
    metricsCount: data.metrics.length,
    timestamp: new Date().toISOString()
  });
  
  // Ensure date is in correct format
  if (!data.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    console.warn(`[API DEBUG] Date format warning: ${data.date} doesn't match YYYY-MM-DD pattern`);
  }
  
  return http.post('/metrics/children/bulk', data);
};

export interface MonthlyAttendanceRecord {
  id: string;
  month: string;        // "YYYY-MM"
  generatedAt: string;  // ISO date
  generatedBy: string;  // user display name
}

export const fetchMonthlyAttendance = async () => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  return http.get<MonthlyAttendanceRecord[]>('/metrics/children/monthly-reports', {
    headers: {
      'x-organization-id': organizationId || ''
    }
  });
};

export const generateMonthlyReport = async (month: string) => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  return http.post<MonthlyAttendanceRecord>(
    '/metrics/children/monthly-report',
    { month },
    { headers: { 'x-organization-id': organizationId || '' } }
  );
};

export const downloadMonthlyReport = async (month: string): Promise<{ blob: Blob; filename: string }> => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  const response = await http.get(`/metrics/children/monthly-report/${month}/download`, {
    responseType: 'blob',
    headers: { 'x-organization-id': organizationId || '' },
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `monthly-report-${month}.pdf`;
  return { blob: response.data, filename };
};

// Add function to fetch daily attendance for all children on a specific date
export const fetchDailyAttendance = async (date?: string) => {
  const params = new URLSearchParams();
  
  // Only add date parameter if it's not today (per updated documentation)
  if (date) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedToday = `${year}-${month}-${day}`;
    
    // Only add date param if it's different from today
    if (date !== formattedToday) {
      params.append('date', date);
      console.log(`[API DEBUG] Fetching daily attendance for specific date: ${date}`);
    } else {
      console.log(`[API DEBUG] Fetching daily attendance for today (no date param needed)`);
    }
  } else {
    console.log(`[API DEBUG] No date provided, fetching today's data (no date param needed)`);
  }
  
  // Include organization ID in headers if available
  const organizationId = localStorage.getItem('currentOrganizationId');
  
  return http.get('/metrics/children/daily', {
    params,
    headers: {
      'x-organization-id': organizationId || ''
    }
  });
};

// Add new types and API endpoints for group access control

export enum GroupAccessRole {
  Admin = 'Admin',
  Member = 'Member',
  ReadOnly = 'ReadOnly',
}

// Legacy interface for backward compatibility
export interface GroupMemberAccess {
  userId: string;
  accessRole: GroupAccessRole;
}

// New interface that matches the API response format for group members
export interface GroupMemberResponse {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  access_role: GroupAccessRole;
  is_teacher?: boolean;
}

// Get group members with access roles
export const fetchGroupMembers = async (groupId: string) => {
  try {
    // Get the current institution ID from localStorage
    const currentInstitutionString = localStorage.getItem('currentInstitution');
    const currentInstitution = currentInstitutionString ? JSON.parse(currentInstitutionString) : null;
    const institutionId = currentInstitution?.id || '';
    
    if (!institutionId) {
      console.warn('No institution ID found in localStorage when fetching group members');
      return { data: [] };
    }
    
    // Create params with institutionId
    const params = new URLSearchParams();
    params.append('institutionId', institutionId);
    
    return http.get<GroupMemberResponse[]>(`/groups/${groupId}/members`, { params });
  } catch (error) {
    console.error('Error fetching group members:', error);
    throw error;
  }
};

// Set access role for a user in a group - updated to use new endpoint
export const setGroupMemberAccess = async (
  groupId: string,
  userId: string,
  accessRole: GroupAccessRole,
  isTeacher: boolean = false
) => {
  try {
    // Get the current institution ID from localStorage
    const currentInstitutionString = localStorage.getItem('currentInstitution');
    const currentInstitution = currentInstitutionString ? JSON.parse(currentInstitutionString) : null;
    const institutionId = currentInstitution?.id || '';
    
    if (!institutionId) {
      console.warn('No institution ID found in localStorage when setting group member access');
      throw new Error('Institution ID is required');
    }
    
    // Create params with institutionId
    const params = new URLSearchParams();
    params.append('institutionId', institutionId);
    
    console.log(`Setting access role "${accessRole}" for user ${userId} in group ${groupId} (isTeacher: ${isTeacher})`);
    
    return http.post(`/groups/${groupId}/access`, { 
    userId, 
      accessRole,
      isTeacher
  }, {
      params,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  } catch (error) {
    console.error('Error setting group member access:', error);
    throw error;
  }
};

// Remove a user's access from a group - updated to use new endpoint
export const removeGroupMemberAccess = async (
  groupId: string,
  userId: string
) => {
  try {
    // Get the current institution ID from localStorage
    const currentInstitutionString = localStorage.getItem('currentInstitution');
    const currentInstitution = currentInstitutionString ? JSON.parse(currentInstitutionString) : null;
    const institutionId = currentInstitution?.id || '';
    
    if (!institutionId) {
      console.warn('No institution ID found in localStorage when removing group member access');
      throw new Error('Institution ID is required');
    }
    
    // Create params with institutionId
    const params = new URLSearchParams();
    params.append('institutionId', institutionId);
    
    return http.delete(`/groups/${groupId}/access/${userId}`, {
      params
    });
  } catch (error) {
    console.error('Error removing group member access:', error);
    throw error;
  }
};

// Activity interface for backward compatibility with calendar components
export interface Activity {
  _id: string;
  name: string;
  description: string;
  time_interval: {
    start: string;
    end: string;
    repeat?: 'none' | 'daily' | 'weekly' | 'monthly' | 'custom';
  };
  reference?: string;
}

// Wrapper function for backward compatibility
export const fetchGroupActivitiesRange = async (
  groupId: string,
  startDate: string,
  endDate: string
) => {
  return fetchGroupActivities(groupId, startDate, endDate);
};

// Update user institution roles
export interface UpdateUserRolesPayload {
  rolesToAdd: string[];
  rolesToRemove: string[];
}

/**
 * Updates the roles for a user using the new API
 * @param userId - The ID of the user to update roles for
 * @param payload - Object containing rolesToAdd and rolesToRemove arrays
 */
export const updateUserRoles = async (userId: string, payload: UpdateUserRolesPayload) => {
  return http.put(`/users/${userId}/roles`, payload, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

/**
 * Fetch available organization roles
 */
export const fetchOrganizationRoles = async () => {
  // Get the current organization ID from localStorage
  const organizationId = localStorage.getItem('currentOrganizationId');
  console.log(`Fetching available roles for organization ${organizationId}`);
  
  return http.get<string[]>('/organizations/roles', {
    headers: {
      'X-Organization-ID': organizationId || ''
    }
  });
};

/**
 * Fetch roles for a specific user in the current organization
 * @param userId - The ID of the user
 * @returns string[] - Array of role names for the user in the current organization
 */
export const fetchUserRoles = async (userId: string) => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  return http.get<string[]>(`/users/${userId}/roles`, {
    headers: {
      'X-Organization-ID': organizationId || ''
    }
  });
};

export interface Institution {
  id: string;
  name: string;
  organization_id?: string;
  organizationId?: string; // Backend uses this field name
  created_at?: string;
  updated_at?: string;
  roles?: string[]; // User's roles within this institution
  isCurrent?: boolean; // Whether this is the current active institution
}

export interface InstitutionPayload {
  name: string;
}

// Global cache for institutions to prevent duplicate API calls
const institutionsCache: {
  data: Institution[] | null;
  loading: boolean;
  promise: Promise<Institution[]> | null;
  lastFetch: number;
  lastOrganizationId: string | null;
} = {
  data: null,
  loading: false,
  promise: null,
  lastFetch: 0,
  lastOrganizationId: null
};

// Cache duration: 5 minutes
const INSTITUTIONS_CACHE_DURATION = 5 * 60 * 1000;

export const fetchInstitutions = async () => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  
  if (!organizationId) {
    console.warn('No organization ID found, cannot fetch institutions');
    return { data: [] };
  }

  const now = Date.now();
  const isCacheValid = institutionsCache.data && 
    (now - institutionsCache.lastFetch) < INSTITUTIONS_CACHE_DURATION &&
    institutionsCache.lastOrganizationId === organizationId;

  // If we have valid cached data for the same organization, use it immediately
  if (isCacheValid) {
    console.log('[INSTITUTIONS] Using cached data for organization:', organizationId);
    return { data: institutionsCache.data! };
  }

  // If there's already a request in progress for the same organization, wait for it
  if (institutionsCache.loading && institutionsCache.promise && institutionsCache.lastOrganizationId === organizationId) {
    console.log('[INSTITUTIONS] Request in progress for same organization, waiting...');
    const data = await institutionsCache.promise;
    return { data };
  }

  console.log('[INSTITUTIONS] Loading institutions data for organization:', organizationId);
  
  // Set loading state and cache the promise
  institutionsCache.loading = true;
  institutionsCache.lastOrganizationId = organizationId;
  
  const loadInstitutions = async (): Promise<Institution[]> => {
    try {
      // Use skip-org-headers to prevent automatic header injection
      const response = await http.get<Institution[]>('/organizations/institutions', {
        headers: {
          'skip-org-headers': 'true'
        }
      });

      // Update cache
      institutionsCache.data = response.data;
      institutionsCache.lastFetch = Date.now();
      institutionsCache.loading = false;
      institutionsCache.promise = null;

      console.log('[INSTITUTIONS] Institutions loaded and cached for organization:', organizationId, 'Count:', response.data.length);
      return response.data;
    } catch (error) {
      institutionsCache.loading = false;
      institutionsCache.promise = null;
      console.error('Error fetching institutions:', error);
      throw error;
    }
  };

  try {
    institutionsCache.promise = loadInstitutions();
    const data = await institutionsCache.promise;
    return { data };
  } catch (error) {
    console.error('Error fetching institutions:', error);
    // Re-throw the error to be handled by the UI
    throw error;
  }
};

export const fetchInstitution = async (id: string) => {
  return http.get<Institution>(`/organizations/institutions/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const createInstitution = async (institution: InstitutionPayload) => {
  return http.post<Institution>('/organizations/institutions', institution, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const updateInstitution = async (id: string, institution: InstitutionPayload) => {
  return http.put<Institution>(`/organizations/institutions/${id}`, institution, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const deleteInstitution = async (id: string) => {
  return http.delete<void>(`/organizations/institutions/${id}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Interface for the new institution users API response
export interface InstitutionUsersResponse {
  success: boolean;
  institution: {
    id: string;
    name: string;
    organizationId: string;
  };
  users: InstitutionUser[];
  total: number;
  accessTypes: Record<string, string>;
}

export interface InstitutionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  accessType: string[];
  roles: InstitutionUserRole[];
}

export interface InstitutionUserRole {
  name: string;
  displayName: string;
  scopeType: string;
  scopeId: string | null;
  grantedAt: string;
  expiresAt: string | null;
}

// Legacy interface for backward compatibility
export interface InstitutionRoleUser {
  userId?: string;
  user_id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  role_name?: string;
  institution_id?: string;
  id?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
  };
}

// Fetch institution users with new API endpoint
export const fetchInstitutionUsers = async (institutionId: string): Promise<InstitutionUsersResponse> => {
  try {
    if (!institutionId) {
      console.warn('fetchInstitutionUsers called without institutionId');
      return { success: false, users: [], total: 0, institution: { id: '', name: '', organizationId: '' }, accessTypes: {} };
    }
    
    const token = localStorage.getItem('token');
    const response = await http.get<InstitutionUsersResponse>(`/organizations/institutions/${institutionId}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'skip-org-headers': 'true'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error in fetchInstitutionUsers:', error);
    // Return empty response structure instead of throwing to avoid breaking UI
    return { success: false, users: [], total: 0, institution: { id: '', name: '', organizationId: '' }, accessTypes: {} };
  }
};

// Legacy function for backward compatibility (deprecated)
export const fetchInstitutionRolesUsers = async (institutionId: string) => {
  console.warn('fetchInstitutionRolesUsers is deprecated, use fetchInstitutionUsers instead');
  try {
    if (!institutionId) {
      console.warn('fetchInstitutionRolesUsers called without institutionId');
      return { data: [] };
    }
    
    const response = await http.get<InstitutionRoleUser[]>(`/roles-management/institution-roles/users`, {
      params: { institutionId }
    });
    
    // Ensure the response data is an array
    if (!Array.isArray(response.data)) {
      console.warn('fetchInstitutionRolesUsers: API response is not an array', response.data);
      return { data: [] };
    }
    
    return response;
  } catch (error) {
    console.error('Error in fetchInstitutionRolesUsers:', error);
    // Return empty array instead of throwing to avoid breaking UI
    return { data: [] };
  }
};

// Fetch available roles for institutions
export const fetchInstitutionRoles = async () => {
  const organizationId = localStorage.getItem('currentOrganizationId');
  return http.get<{ id: number; name: string; description: string }[]>('/roles-management/roles', {
    headers: {
      'X-Organization-ID': organizationId || '',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });
};

// Update roles for a user in an institution
export const updateUserInstitutionRoles = async (
  institutionId: string, 
  userId: string, 
  roleIds: number[]
) => {
  // Create the userRoles array with objects containing userId and roleId
  const userRoles = roleIds.map(roleId => ({
    userId,
    roleId
  }));
  
  // Make the PUT request to the correct endpoint with the proper payload format
  return http.put(`/roles-management/institution-roles/users`, {
    userRoles
  }, {
    params: { institutionId }
  });
};

// Restore fetchChildren function that was accidentally removed
export const fetchChildren = async () => {
  return http.get<{ kids: Kid[] }>('/kids/all');
};

// Fetch kids count - optimized endpoint that returns only counts
export interface KidsCountResponse {
  total: number;
  signedIn: number;
  notSignedIn: number;
}

export const fetchKidsCount = async (): Promise<KidsCountResponse> => {
  const response = await http.get<KidsCountResponse>('/kids/count');
  return response.data;
};

// Define the StaffUserRepresentation interface here as well for reuse
export interface StaffUserRepresentation extends UserRepresentation {
  roles?: string[];
}

// Organization interface  
export interface Organization {
  id: string;
  name: string;
  status?: string;
  joinedAt?: string;
  isCurrent?: boolean;
  isDefault?: boolean;
  roles?: string[];
  permissions?: string[];
  institutionCount?: number;
  institutions?: Institution[];
  currentInstitution?: string; // ID of current institution
}

// New API functions for user-specific organizations (now includes institutions)
export interface UserOrganizationsResponse {
  success: boolean;
  organizations: Organization[]; // Each organization now includes its institutions
  currentOrganization: string | null; // Backend returns organization ID, not full object
  total: number;
  error?: string;
}


export const fetchUserOrganizations = async (): Promise<UserOrganizationsResponse> => {
  try {
    const response = await http.get<UserOrganizationsResponse>('/users/organizations', {
      headers: {
        'skip-org-headers': 'true' // Skip automatic X-Organization-ID header
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    throw error;
  }
};


// Unified endpoint for updating current organization and/or institution
// POST /v1/users/current
// Options:
// - Update only organization (clears institution): { organizationId: "org-uuid" }
// - Update only institution (keeps current organization): { institutionId: "inst-uuid" }
// - Update both at once: { organizationId: "org-uuid", institutionId: "inst-uuid" }

export interface UpdateCurrentContextRequest {
  organizationId?: string;
  institutionId?: string;
}

export interface UpdateCurrentContextResponse {
  success: boolean;
  message?: string;
  currentOrganization?: string;
  currentInstitution?: string;
  error?: string;
}

export const updateCurrentContext = async (payload: UpdateCurrentContextRequest): Promise<UpdateCurrentContextResponse> => {
  try {
    console.log('[USER_CONTEXT] Updating current context:', payload);
    
    const response = await http.post<UpdateCurrentContextResponse>('/users/current', payload, {
      headers: {
        'skip-org-headers': 'true' // Signal to skip automatic org/institution headers
      }
    });
    
    // Update successful
    if (response.data.success) {
      console.log('[USER_CONTEXT] Current context updated successfully:', response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error updating current context:', error);
    throw error;
  }
};

// Helper function for updating only institution (keeps current organization)
export const updateCurrentInstitution = async (institutionId: string): Promise<UpdateCurrentContextResponse> => {
  console.log('[USER_INSTITUTIONS] Updating current institution to:', institutionId);
  return updateCurrentContext({ institutionId });
};

// Helper function for updating only organization (clears institution)
export const updateCurrentOrganization = async (organizationId: string): Promise<UpdateCurrentContextResponse> => {
  console.log('[USER_ORGANIZATIONS] Updating current organization to:', organizationId);
  return updateCurrentContext({ organizationId });
};

export const fetchStaff = async () => {
  try {
    console.log('Fetching staff without institution/organization headers');
    
    const response = await http.get<StaffUserRepresentation[]>('/users/staff', {
      headers: {
        'skip-org-headers': 'true'
      }
    });
    
    // Ensure response.data is always an array
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid response from staff API:', response.data);
      // Return empty array if data is invalid
      return { data: [] };
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching staff:', error);
    // Re-throw the error to be handled by React Query
    throw error;
  }
};

// Cache clearing utilities - useful for logout or organization changes
export const clearAllApiCaches = () => {
  groupsCache.data = null;
  groupsCache.loading = false;
  groupsCache.promise = null;
  groupsCache.lastFetch = 0;
  groupsCache.lastInstitutionId = null;
  
  institutionsCache.data = null;
  institutionsCache.loading = false;
  institutionsCache.promise = null;
  institutionsCache.lastFetch = 0;
  institutionsCache.lastOrganizationId = null;
  
  console.log('[CACHE] All API caches cleared');
};


// Debug function to check cache status
export const getCacheStats = () => {
  const now = Date.now();
  return {
    institutions: {
      hasData: !!institutionsCache.data,
      age: institutionsCache.lastFetch ? now - institutionsCache.lastFetch : null,
      loading: institutionsCache.loading,
      orgId: institutionsCache.lastOrganizationId
    }
  };
};

// Clear cache specifically when switching organizations
export const clearOrganizationCache = () => {
  // Clear institutions cache since it's organization-specific
  institutionsCache.data = null;
  institutionsCache.loading = false;
  institutionsCache.promise = null;
  institutionsCache.lastFetch = 0;
  institutionsCache.lastOrganizationId = null;

  // Clear groups cache since it's institution-specific and institutions are changing
  groupsCache.data = null;
  groupsCache.loading = false;
  groupsCache.promise = null;
  groupsCache.lastFetch = 0;
  groupsCache.lastInstitutionId = null;

  console.log('[CACHE] Organization-specific caches cleared');
};


export const clearInstitutionsCache = () => {
  institutionsCache.data = null;
  institutionsCache.loading = false;
  institutionsCache.promise = null;
  institutionsCache.lastFetch = 0;
  console.log('[CACHE] Institutions cache cleared');
};

// Organization Users API
export interface OrganizationUserRole {
  name: string;
  displayName: string;
  scopeType: 'organization' | 'institution';
  institutionName?: string;
  grantedAt: string;
}

export interface OrganizationUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  emailVerified: boolean;
  roles: OrganizationUserRole[];
  institutions: string[];
  hasOrganizationRole: boolean;
  hasInstitutionRole: boolean;
}

export interface OrganizationUsersResponse {
  success: boolean;
  organizationId: string;
  users: OrganizationUser[];
  total: number;
}

export interface FetchOrganizationUsersParams {
  role?: string;
  institutionId?: string;
  scope?: 'organization' | 'institution';
  search?: string;
  status?: 'active' | 'inactive' | 'all';
}

export const fetchOrganizationUsers = async (params?: FetchOrganizationUsersParams): Promise<OrganizationUsersResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.role) queryParams.append('role', params.role);
  if (params?.institutionId) queryParams.append('institutionId', params.institutionId);
  if (params?.scope) queryParams.append('scope', params.scope);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  
  const response = await http.get<OrganizationUsersResponse>(`/organizations/users?${queryParams.toString()}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
  
  return response.data;
};

// Organization Company Details API
export interface OrganizationCompanyDetails {
  company_name?: string;
  pib?: string;
  mb?: string;
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  address_state_province?: string;
  phone?: string;
  email?: string;
  website?: string;
  business_type?: string;
  /** Days after end of billing month until invoice is due. null = use fallback (org or global 30). */
  payment_terms?: number | null;
}

export interface OrganizationBankAccount {
  id: string;
  organization_id: string;
  bank_name: string;
  account_number: string;
  iban?: string;
  swift_bic?: string;
  currency: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExtendedOrganization extends OrganizationCompanyDetails {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  bank_accounts?: OrganizationBankAccount[];
}

// API Response wrapper for organization details
export interface OrganizationDetailsResponse {
  success: boolean;
  data: ExtendedOrganization;
}

// Get organization details with company information
export const fetchOrganizationDetails = async () => {
  return http.get<OrganizationDetailsResponse>('/organizations/details', {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Update organization company details
export const updateOrganizationCompanyDetails = async (details: OrganizationCompanyDetails) => {
  return http.put<ExtendedOrganization>('/organizations/company-details', details, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Organization Bank Account API
export interface CreateBankAccountData {
  bank_name: string;
  account_number: string;
  iban?: string;
  swift_bic?: string;
  currency?: string;
  is_primary?: boolean;
}

export interface UpdateBankAccountData {
  bank_name?: string;
  account_number?: string;
  iban?: string;
  swift_bic?: string;
  currency?: string;
  is_primary?: boolean;
  is_active?: boolean;
}

// Get organization bank accounts
export const fetchOrganizationBankAccounts = async () => {
  return http.get<{ success: boolean; data: OrganizationBankAccount[] }>('/organizations/bank-accounts', {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Create organization bank account
export const createOrganizationBankAccount = async (data: CreateBankAccountData) => {
  return http.post<{ success: boolean; data: OrganizationBankAccount }>('/organizations/bank-accounts', data, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Update organization bank account
export const updateOrganizationBankAccount = async (accountId: string, data: UpdateBankAccountData) => {
  return http.put<{ success: boolean; data: OrganizationBankAccount }>(`/organizations/bank-accounts/${accountId}`, data, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Delete organization bank account
export const deleteOrganizationBankAccount = async (accountId: string) => {
  return http.delete(`/organizations/bank-accounts/${accountId}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Institution Company Details API
export interface InstitutionCompanyDetails extends OrganizationCompanyDetails {
  inherit_from_organization?: boolean;
}

export interface InstitutionBankAccount {
  id: string;
  institution_id: string;
  bank_name: string;
  account_number: string;
  iban?: string;
  swift_bic?: string;
  currency: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExtendedInstitution extends InstitutionCompanyDetails {
  id: string;
  name: string;
  organization_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  bank_accounts?: InstitutionBankAccount[];
  effective_details?: OrganizationCompanyDetails;
  effective_bank_accounts?: InstitutionBankAccount[];
}

export interface InstitutionDetailsResponse {
  success: boolean;
  data: ExtendedInstitution;
}

// Get institution details
export const fetchInstitutionDetails = async (institutionId: string) => {
  return http.get<InstitutionDetailsResponse>(`/organizations/institutions/${institutionId}/details`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Update institution company details
export const updateInstitutionCompanyDetails = async (institutionId: string, details: InstitutionCompanyDetails) => {
  return http.put<{ success: boolean; data: ExtendedInstitution }>(`/organizations/institutions/${institutionId}/details`, details, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Institution Bank Account API
export const fetchInstitutionBankAccounts = async (institutionId: string) => {
  return http.get<{ success: boolean; data: InstitutionBankAccount[] }>(`/organizations/institutions/${institutionId}/bank-accounts`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const createInstitutionBankAccount = async (institutionId: string, data: CreateBankAccountData) => {
  return http.post<{ success: boolean; data: InstitutionBankAccount }>(`/organizations/institutions/${institutionId}/bank-accounts`, data, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const updateInstitutionBankAccount = async (institutionId: string, accountId: string, data: UpdateBankAccountData) => {
  return http.put<{ success: boolean; data: InstitutionBankAccount }>(`/organizations/institutions/${institutionId}/bank-accounts/${accountId}`, data, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const deleteInstitutionBankAccount = async (institutionId: string, accountId: string) => {
  return http.delete(`/organizations/institutions/${institutionId}/bank-accounts/${accountId}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

// Note: Bank accounts are now included in the organization details response
// No separate bank account API endpoints needed

// Document API functions
export const fetchKidDocuments = async (
  kidId: string,
  options?: {
    documentType?: DocumentType;
    page?: number;
    limit?: number;
  }
): Promise<{ data: DocumentListResponse }> => {
  const params = new URLSearchParams();
  
  if (options?.documentType) params.append('documentType', options.documentType);
  if (options?.page) params.append('page', options.page.toString());
  if (options?.limit) params.append('limit', options.limit.toString());

  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  const response = await http.get<DocumentListResponse>(`/kids/${kidId}/documents?${params.toString()}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });

  return response;
};

export const uploadKidDocument = async (
  kidId: string,
  documentData: DocumentUploadRequest
): Promise<{ data: KidDocument }> => {
  const formData = new FormData();
  formData.append('file', documentData.file);
  formData.append('documentType', documentData.documentType);
  formData.append('title', documentData.title);
  
  if (documentData.documentCategory) {
    formData.append('documentCategory', documentData.documentCategory);
  }
  if (documentData.isVisibleToParents !== undefined) {
    formData.append('isVisibleToParents', documentData.isVisibleToParents.toString());
  }
  if (documentData.isSensitive !== undefined) {
    formData.append('isSensitive', documentData.isSensitive.toString());
  }

  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  const response = await http.post<KidDocument>(`/kids/${kidId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'skip-org-headers': 'true'
    }
  });

  return response;
};

export const downloadKidDocument = async (
  kidId: string,
  documentId: string
): Promise<Blob> => {
  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  const response = await http.get(`/kids/${kidId}/documents/${documentId}/download`, {
    responseType: 'blob',
    headers: {
      'skip-org-headers': 'true'
    }
  });

  return response.data;
};

export const deleteKidDocument = async (
  kidId: string,
  documentId: string
): Promise<void> => {
  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  await http.delete(`/kids/${kidId}/documents/${documentId}`, {
    headers: {
      'skip-org-headers': 'true'
    }
  });
};

export const updateKidDocument = async (
  kidId: string,
  documentId: string,
  updates: {
    title?: string;
    isVisibleToParents?: boolean;
    isSensitive?: boolean;
    isArchived?: boolean;
  }
): Promise<{ data: KidDocument }> => {
  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  const response = await http.patch<KidDocument>(`/kids/${kidId}/documents/${documentId}`, updates, {
    headers: {
      'skip-org-headers': 'true'
    }
  });

  return response;
};

export const fetchDocumentTypes = async (): Promise<{ data: DocumentTypesResponse }> => {
  // Get headers for organization and institution
  // Note: Using skip-org-headers, no longer need these IDs
  // const organizationId = localStorage.getItem('currentOrganizationId');
  // const savedInstitutionJson = localStorage.getItem('currentInstitution');
  // let institutionId = null;
  
  // Note: Using skip-org-headers, no longer need institution ID parsing
  // if (savedInstitutionJson) {
  //   try {
  //     const savedInstitution = JSON.parse(savedInstitutionJson);
  //     if (savedInstitution && savedInstitution.id) {
  //       institutionId = savedInstitution.id;
  //     }
  //   } catch (e) {
  //     console.error('Failed to parse saved institution:', e);
  //   }
  // }

  const response = await http.get<DocumentTypesResponse>('/kids/documents/types', {
    headers: {
      'skip-org-headers': 'true'
    }
  });

  return response;
};

// ============================================
// ONBOARDING API
// ============================================

export interface OnboardingStepConfig {
  id: string;
  order: number;
  notificationType: string;
  priority: string;
  title: string;
  titleKey?: string;
  message: string;
  messageKey?: string;
  actionUrl: string;
  actionLabel: string;
  actionLabelKey?: string;
  description: string;
  descriptionKey?: string;
}

export interface OnboardingStep {
  id: string;
  organizationId: string;
  stepId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  updatedAt: string;
  config: OnboardingStepConfig;
}

export interface OnboardingProgress {
  totalSteps: number;
  completedSteps: number;
  progress: number;
  currentStep: OnboardingStep;
  steps: OnboardingStep[];
}

export const fetchOnboardingProgress = async (): Promise<OnboardingProgress> => {
  const response = await http.get<OnboardingProgress>('/onboarding/progress');
  return response.data;
};

export const completeOnboardingStep = async (stepId: string): Promise<void> => {
  await http.post(`/onboarding/steps/${stepId}/complete`);
};

// ============================================
// TEMPLATES API (Document Templates - Contracts, Consent Forms, etc.)
// ============================================

export enum TemplateType {
  CONTRACT = 'contract',
  CONSENT = 'consent',
  ENROLLMENT = 'enrollment',
  OTHER = 'other',
}

export enum TemplateCategory {
  // Contracts
  PARENT_CONTRACT = 'parent_contract',
  PARENT_CONTRACT_HOURLY = 'parent_contract_hourly',
  PARENT_CONTRACT_MONTHLY = 'parent_contract_monthly',
  STAFF_CONTRACT = 'staff_contract',
  // Consent forms
  PHOTO_CONSENT = 'photo_consent',
  MEDICAL_CONSENT = 'medical_consent',
  EXCURSION_CONSENT = 'excursion_consent',
  MEDICATION_CONSENT = 'medication_consent',
  // Enrollment
  ENROLLMENT_FORM = 'enrollment_form',
  ENROLLMENT_FORM_DETAILED = 'enrollment_form_detailed',
  // Other
  PICKUP_AUTHORIZATION = 'pickup_authorization',
  EMERGENCY_CONTACT = 'emergency_contact',
  GENERAL = 'general',
}

export interface TemplateVariable {
  type: 'text' | 'date' | 'number' | 'email' | 'phone' | 'address' | 'select';
  label: string;
  labelSr?: string;
  description?: string;
  required?: boolean;
  defaultValue?: string;
  options?: string[];
}

export interface OrganizationTemplate {
  id: string;
  organizationId: string;
  institutionId?: string;
  globalTemplateId?: string;
  isCustom: boolean;
  templateName: string;
  templateType: TemplateType;
  category?: TemplateCategory;
  description?: string;
  s3Bucket: string;
  s3Key: string;
  encryptionIv: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  fileHash: string;
  version: number;
  isActive: boolean;
  previousVersionId?: string;
  requiresParentSignature: boolean;
  requiresInstitutionSignature: boolean;
  isRequiredForEnrollment: boolean;
  validFrom?: string;
  validUntil?: string;
  templateVariables: Record<string, TemplateVariable>;
  customizationNotes?: string;
  createdByUserId: string;
  lastModifiedByUserId?: string;
  createdAt: string;
  updatedAt: string;
  downloadUrl?: string;
}

export interface GlobalTemplate {
  id: string;
  templateName: string;
  templateType: TemplateType;
  category?: TemplateCategory;
  description?: string;
  displayName: string;
  previewImageUrl?: string;
  tags: string[];
  s3Bucket: string;
  s3Key: string;
  fileHash: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  version: number;
  isActive: boolean;
  isFeatured: boolean;
  templateVariables: Record<string, TemplateVariable>;
  instructions?: string;
  legalDisclaimer?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  timesCopied: number;
  lastCopiedAt?: string;
  downloadUrl?: string;
}

export interface OrganizationTemplatesResponse {
  success: boolean;
  data: OrganizationTemplate[];
  total: number;
  page: number;
  limit: number;
}

export interface GlobalTemplatesResponse {
  success: boolean;
  data: GlobalTemplate[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Fetch organization templates
 */
export const fetchOrganizationTemplates = async (params?: {
  templateType?: TemplateType;
  category?: TemplateCategory;
  isRequiredForEnrollment?: boolean;
  isActive?: boolean;
  isCustom?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<OrganizationTemplatesResponse> => {
  const queryParams = new URLSearchParams();
  
  // Don't pass institutionId - it's in the headers now via requireAuth
  if (params?.templateType) queryParams.append('type', params.templateType);
  if (params?.category) queryParams.append('category', params.category);
  if (params?.isRequiredForEnrollment !== undefined) queryParams.append('required', params.isRequiredForEnrollment.toString());
  if (params?.isActive !== undefined) queryParams.append('active', params.isActive.toString());
  if (params?.isCustom !== undefined) queryParams.append('custom', params.isCustom.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const response = await http.get<OrganizationTemplatesResponse>(
    `/templates/organization?${queryParams.toString()}`,
    {
      headers: {
        'skip-org-headers': 'true'
      }
    }
  );
  return response.data;
};

/**
 * Fetch global templates (template library)
 */
export const fetchGlobalTemplates = async (params?: {
  templateType?: TemplateType;
  category?: TemplateCategory;
  isFeatured?: boolean;
  search?: string;
  tags?: string[];
  page?: number;
  limit?: number;
}): Promise<GlobalTemplatesResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.templateType) queryParams.append('type', params.templateType);
  if (params?.category) queryParams.append('category', params.category);
  if (params?.isFeatured !== undefined) queryParams.append('featured', params.isFeatured.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.tags) params.tags.forEach(tag => queryParams.append('tags', tag));
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const response = await http.get<GlobalTemplatesResponse>(
    `/templates/global?${queryParams.toString()}`
  );
  return response.data;
};

/**
 * Download organization template
 */
export const downloadOrganizationTemplate = async (templateId: string): Promise<Blob> => {
  const response = await http.get(`/templates/organization/${templateId}/download`, {
    responseType: 'blob',
    headers: {
      'skip-org-headers': 'true'
    }
  });
  return response.data;
};

/**
 * Generate a filled document from an organization template for a specific child
 */
export interface GenerateDocumentPayload {
  kidId: string;
  variables: Record<string, string>;
}

export interface GenerateDocumentResponse {
  success: boolean;
  documentUrl?: string;
  documentId?: string;
}

export const generateDocument = async (
  templateId: string,
  payload: GenerateDocumentPayload,
): Promise<GenerateDocumentResponse> => {
  const response = await http.post<GenerateDocumentResponse>(
    `/templates/organization/${templateId}/generate`,
    payload,
    {
      headers: {
        'skip-org-headers': 'true',
      },
    },
  );
  return response.data;
};

// ─── Expenses ───────────────────────────────────────────────────────────────

export const EXPENSE_TYPES = [
  'salary_advance',
  'office_supplies',
  'food_and_catering',
  'utilities',
  'maintenance',
  'transport',
  'equipment',
  'cleaning',
  'other',
] as const;

export type ExpenseType = (typeof EXPENSE_TYPES)[number];
export type ExpenseStatus = 'draft' | 'pending' | 'approved' | 'rejected';

export interface ExpenseTypesResponse {
  types: string[];
  currencies: string[];
}

export const fetchExpenseTypes = async (): Promise<ExpenseTypesResponse> => {
  const response = await http.get<ExpenseTypesResponse>('/expenses/types', {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export interface ExpenseAttachment {
  s3Key: string;
  fileName: string;
  contentType: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

export interface Expense {
  _id: string;
  expenseNumber: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  expenseDate: string;
  type: ExpenseType;
  status: ExpenseStatus;
  attachments: ExpenseAttachment[];
  submittedBy: string;
  submittedByName?: string;
  createdAt: string;
  rejectionReason?: string;
  rejectedBy?: string;
  rejectedByName?: string;
  rejectedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
}

export interface ExpensePagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ExpenseListResponse {
  data: Expense[];
  pagination: ExpensePagination;
}

export interface CreateExpensePayload {
  title: string;
  description?: string;
  amount: number;
  currency?: string;
  expenseDate: string;
  type: ExpenseType;
}

export type UpdateExpensePayload = Partial<CreateExpensePayload>;

export interface ExpenseListParams {
  status?: ExpenseStatus;
  type?: ExpenseType;
  from?: string;
  to?: string;
  submittedBy?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'expenseDate' | 'amount' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export const fetchExpenses = async (params?: ExpenseListParams): Promise<ExpenseListResponse> => {
  const response = await http.get<ExpenseListResponse>('/expenses', {
    params,
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const createExpense = async (payload: CreateExpensePayload): Promise<Expense> => {
  const response = await http.post<Expense>('/expenses', payload, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const updateExpense = async (id: string, payload: UpdateExpensePayload): Promise<Expense> => {
  const response = await http.put<Expense>(`/expenses/${id}`, payload, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const deleteExpense = async (id: string): Promise<void> => {
  await http.delete(`/expenses/${id}`, {
    headers: { 'skip-org-headers': 'true' },
  });
};

export const submitExpense = async (id: string): Promise<Expense> => {
  const response = await http.post<Expense>(`/expenses/${id}/submit`, {}, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const approveExpense = async (id: string): Promise<Expense> => {
  const response = await http.post<Expense>(`/expenses/${id}/approve`, {}, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const rejectExpense = async (id: string, reason: string): Promise<Expense> => {
  const response = await http.post<Expense>(`/expenses/${id}/reject`, { reason }, {
    headers: { 'skip-org-headers': 'true' },
  });
  return response.data;
};

export const uploadExpenseAttachment = async (
  expenseId: string,
  file: File,
): Promise<ExpenseAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await http.post<ExpenseAttachment>(
    `/expenses/${expenseId}/attachments`,
    formData,
    { headers: { 'skip-org-headers': 'true', 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const downloadExpenseAttachment = async (
  expenseId: string,
  s3Key: string,
): Promise<Blob> => {
  const response = await http.get(
    `/expenses/${expenseId}/attachments/${encodeURIComponent(s3Key)}`,
    { responseType: 'blob', headers: { 'skip-org-headers': 'true' } },
  );
  return response.data;
};

export const deleteExpenseAttachment = async (
  expenseId: string,
  s3Key: string,
): Promise<void> => {
  await http.delete(`/expenses/${expenseId}/attachments/${encodeURIComponent(s3Key)}`, {
    headers: { 'skip-org-headers': 'true' },
  });
};

// ─── End Expenses ────────────────────────────────────────────────────────────

/**
 * Copy global template to organization
 */
export const copyGlobalTemplate = async (data: {
  globalTemplateId: string;
  institutionId?: string;
  customizationNotes?: string;
  templateName?: string;
  description?: string;
  requiresParentSignature?: boolean;
  requiresInstitutionSignature?: boolean;
  isRequiredForEnrollment?: boolean;
}): Promise<{ success: boolean; data: OrganizationTemplate }> => {
  const response = await http.post<{ success: boolean; data: OrganizationTemplate }>(
    '/templates/organization/copy',
    data,
    {
      headers: {
        'skip-org-headers': 'true'
      }
    }
  );
  return response.data;
};